import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { admin, errorMessage } from "../lib/api";
import { useCurrency } from "../lib/auth";
import { fromMinor, money, toMinor } from "../lib/format";
import type { DiscountCode, List } from "../lib/types";
import { Alert, Badge, Button, Field, Modal, PageHeader, Spinner, Table, Toggle } from "../components/ui";

type Form = { id?: string; code: string; kind: DiscountCode["kind"]; value: string; min_subtotal: string; usage_limit: string; ends_at: string; active: boolean };
const blank: Form = { code: "", kind: "percent", value: "10", min_subtotal: "", usage_limit: "", ends_at: "", active: true };

export default function Discounts() {
  const qc = useQueryClient();
  const currency = useCurrency();
  const list = useQuery({ queryKey: ["discounts"], queryFn: () => admin.get<List<DiscountCode>>("/discount-codes?limit=250") });
  const [form, setForm] = useState<Form | null>(null);
  const [error, setError] = useState<string>();

  const save = useMutation({
    mutationFn: (f: Form) => {
      const value = f.kind === "percent" ? Math.round(Number(f.value.replace(",", ".")) * 100) : f.kind === "fixed" ? toMinor(f.value) ?? 0 : 0;
      const body = {
        code: f.code.trim().toUpperCase(), kind: f.kind, value, min_subtotal: toMinor(f.min_subtotal) ?? 0, active: f.active,
        ...(f.usage_limit ? { usage_limit: Number(f.usage_limit) } : {}),
        ...(f.ends_at ? { ends_at: new Date(f.ends_at).toISOString() } : {}),
      };
      return f.id ? admin.patch(`/discount-codes/${f.id}`, body) : admin.post("/discount-codes", body);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["discounts"] }); setForm(null); },
    onError: (e) => setError(errorMessage(e)),
  });
  const del = useMutation({ mutationFn: (id: string) => admin.del(`/discount-codes/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["discounts"] }) });

  const describe = (d: DiscountCode) =>
    d.kind === "percent" ? `${d.value / 100}%` : d.kind === "fixed" ? money(d.value, currency) : "Transport gratuit";
  const rows = list.data?.data ?? [];

  return (
    <>
      <PageHeader title="Coduri de reducere" actions={<Button onClick={() => { setError(undefined); setForm(blank); }}>+ Cod nou</Button>} />
      {list.isLoading ? <Spinner /> : (
        <Table head={["Cod", "Reducere", "Minim comandă", "Utilizări", "Expiră", "Status", ""]} empty={rows.length === 0}>
          {rows.map((d) => (
            <tr key={d.id} className="hover:bg-slate-50">
              <td className="px-4 py-3 font-mono font-bold">{d.code}</td>
              <td className="px-4 py-3">{describe(d)}</td>
              <td className="px-4 py-3">{d.min_subtotal ? money(d.min_subtotal, currency) : "—"}</td>
              <td className="px-4 py-3">{d.used_count}{d.usage_limit != null && ` / ${d.usage_limit}`}</td>
              <td className="px-4 py-3 text-slate-500">{d.ends_at ? new Date(d.ends_at).toLocaleDateString("ro-RO") : "—"}</td>
              <td className="px-4 py-3">{d.active ? <Badge tone="green">Activ</Badge> : <Badge>Inactiv</Badge>}</td>
              <td className="px-4 py-3 text-right">
                <Button size="sm" variant="ghost" onClick={() => {
                  setError(undefined);
                  setForm({
                    id: d.id, code: d.code, kind: d.kind, active: d.active,
                    value: d.kind === "percent" ? String(d.value / 100) : fromMinor(d.value),
                    min_subtotal: d.min_subtotal ? fromMinor(d.min_subtotal) : "", usage_limit: d.usage_limit?.toString() ?? "",
                    ends_at: d.ends_at ? d.ends_at.slice(0, 16) : "",
                  });
                }}>Editează</Button>
                <Button size="sm" variant="ghost" onClick={() => confirm(`Ștergi codul ${d.code}?`) && del.mutate(d.id)}>Șterge</Button>
              </td>
            </tr>
          ))}
        </Table>
      )}
      <Modal open={!!form} onClose={() => setForm(null)} title={form?.id ? "Editează codul" : "Cod de reducere nou"}>
        {form && (
          <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(form); }}>
            <Field label="Cod"><input className="input font-mono uppercase" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="VARA20" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tip">
                <select className="input" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as Form["kind"] })}>
                  <option value="percent">Procent</option>
                  <option value="fixed">Sumă fixă</option>
                  <option value="free_shipping">Transport gratuit</option>
                </select>
              </Field>
              {form.kind !== "free_shipping" && (
                <Field label={form.kind === "percent" ? "Procent (%)" : "Sumă (lei)"}>
                  <input className="input" required inputMode="decimal" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
                </Field>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Comandă minimă (lei)"><input className="input" inputMode="decimal" value={form.min_subtotal} onChange={(e) => setForm({ ...form, min_subtotal: e.target.value })} /></Field>
              <Field label="Limită utilizări"><input className="input" inputMode="numeric" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} placeholder="nelimitat" /></Field>
            </div>
            <Field label="Expiră la (opțional)"><input className="input" type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} /></Field>
            <Toggle label="Activ" checked={form.active} onChange={(v) => setForm({ ...form, active: v })} />
            {error && <Alert>{error}</Alert>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setForm(null)}>Anulează</Button>
              <Button disabled={save.isPending}>Salvează</Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}
